import { useState } from 'react';
import type { PresenceStatus } from '@orbit-sync/shared-types';
import { useDirectoryStore } from '../chat/directoryStore.js';
import { avatarUrl } from '../lib/api/files.js';
import { Icon } from './Icon.js';

/**
 * A person's picture, or their initials, with an optional presence ring.
 *
 * The picture comes from the directory cache: whoever has an `avatarUpdatedAt`
 * there has one, and the timestamp makes its URL change when it does. Without
 * one, identity is carried by the initials and a hue derived from the user id -
 * a pure function of the id, so the same person is the same colour everywhere.
 */

/** FNV-1a, folded to a hue. Cheap, stable, and spreads short ids well. */
function hueFor(seed: string): number {
  let hash = 0x811c9dc5;

  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }

  return Math.abs(hash) % 360;
}

/** Up to two letters: initials for a multi-word name, else the leading letters. */
function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return '?';
  }

  if (words.length === 1) {
    return (words[0] ?? '').slice(0, 2).toUpperCase();
  }

  const first = words[0]?.[0] ?? '';
  const last = words[words.length - 1]?.[0] ?? '';

  return `${first}${last}`.toUpperCase();
}

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface AvatarProps {
  name: string;
  /** Seeds the colour. Falls back to the name when there is no id to hand. */
  id?: string;
  size?: AvatarSize;
  /** `null` renders the hollow "unknown" ring; omit it to show no ring at all. */
  presence?: PresenceStatus | null | undefined;
  /** Renders the group glyph instead of initials. */
  group?: boolean;
  /** Renders the "Saved messages" bookmark. */
  saved?: boolean;
  /** Overrides the directory's picture - a preview of one not yet saved. */
  src?: string | null;
}

const PRESENCE_LABEL: Record<PresenceStatus, string> = {
  online: 'Online',
  away: 'Away',
  offline: 'Offline',
};

export function Avatar({
  name,
  id,
  size = 'md',
  presence,
  group = false,
  saved = false,
  src,
}: AvatarProps) {
  const updatedAt = useDirectoryStore((state) =>
    id && !group ? (state.names[id]?.avatarUpdatedAt ?? null) : null,
  );
  const url =
    src !== undefined
      ? src
      : id && updatedAt
        ? avatarUrl({ id, avatarUpdatedAt: updatedAt })
        : null;
  // A picture that fails to load falls back to initials rather than a broken image.
  const [failed, setFailed] = useState<string | null>(null);
  const showImage = url !== null && failed !== url;
  const classes = ['avatar'];

  if (size !== 'md') {
    classes.push(`avatar--${size}`);
  }

  if (group) {
    classes.push('avatar--group');
  }

  if (saved) {
    classes.push('avatar--saved');
  }

  // `undefined` means "this caller does not show presence"; `null` means "we
  // genuinely do not know", which is a different thing and gets a hollow ring.
  const showPresence = presence !== undefined;
  const label = presence ? PRESENCE_LABEL[presence] : 'Presence unknown';

  return (
    <span
      className={classes.join(' ')}
      style={group || saved ? undefined : { ['--avatar-hue' as string]: hueFor(id ?? name) }}
      aria-hidden="true"
    >
      {saved ? (
        <Icon name="bookmark" size={size === 'sm' || size === 'xs' ? 13 : 16} />
      ) : group ? (
        <Icon name="users" size={size === 'sm' || size === 'xs' ? 13 : 16} />
      ) : showImage ? (
        <img className="avatar-img" src={url} alt="" onError={() => setFailed(url)} />
      ) : (
        initialsOf(name)
      )}

      {showPresence ? (
        <span
          className={`avatar-presence avatar-presence--${presence ?? 'unknown'}`}
          title={label}
        />
      ) : null}
    </span>
  );
}
